import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatasetClientService } from '@mode/dataset/data-access';
import { DataViewFacade, SwitchViewFacade } from '@mode/switch-views/contract';
import { ToastService } from '@mode/shared/util-toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, combineLatestWith, concatMap, map, of, switchMap, tap, filter, withLatestFrom } from 'rxjs';
import {
  DatasetActions,
  DatasetPublishingFlagStates,
  PublishDialogService,
  RepublishDialogService,
  UnpublishDialogService,
} from '@mode/dataset/contract';
import { DatasetService } from './dataset.service';
import { ConfirmDialog, FeatureFlag, FeatureFlagsFacade, ProducerTypes } from '@mode/shared/contract-common';
import { DatasetPublishSuccessComponent } from '@mode/dataset/util';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { CellExportFacade } from '@mode/notebooks/contract';
import { QueryResource } from 'libs/shared/data-access-webapp/src/lib/queries/query-response.types';
import { CellExportActions } from '@mode/notebooks/contract';
import { MoveSpacePublishService } from '../move-space-publish/move-space-publish.service';

@Injectable()
export class DatasetEffects {
  deleteDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.deleteDataset),
      concatMap((action) =>
        this.datasetService.deleteDataset(action.datasetToken).pipe(
          map(() => DatasetActions.deleteDatasetSuccess()),
          catchError(() => of(DatasetActions.deleteDatasetFail()))
        )
      )
    )
  );

  deleteDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.deleteDatasetSuccess),
        tap(() => this.toastService.success('Deleted Dataset'))
      ),
    { dispatch: false }
  );

  deleteDatasetError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.deleteDatasetFail),
        tap(() => this.toastService.error('Failed to delete Dataset'))
      ),
    { dispatch: false }
  );

  loadDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DatasetActions.loadDatasetViewRoute,
        DatasetActions.loadDatasetEditRoute,
        DatasetActions.runDatasetPollingCompleted
      ),
      switchMap(({ datasetToken }) =>
        this.getDataset(datasetToken).pipe(
          map((dataset) =>
            DatasetActions.loadDatasetSuccess({
              token: datasetToken,
              datasetResult: dataset.datasetResult,
              switchView: dataset.switchView,
              details: dataset.details,
            })
          ),
          catchError((error: HttpErrorResponse) => of(DatasetActions.loadDatasetFailure({ error })))
        )
      )
    )
  );

  loadImportedDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.loadDatasetReportEditRoute),
      switchMap(({ datasetToken }) =>
        this.getDataset(datasetToken).pipe(
          map((dataset) =>
            DatasetActions.loadDatasetReportEditRouteSuccess({
              dataset: {
                token: datasetToken,
                result: dataset.datasetResult,
                switchView: dataset.switchView,
                details: dataset.details,
              },
            })
          )
        )
      )
    )
  );

  loadNotebookCellDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.loadNotebookCellDatasetEditRoute),
      switchMap(({ datasetToken, switchViewToken }) =>
        this.getNotebookCellDataset(datasetToken, switchViewToken).pipe(
          map((dataset) =>
            DatasetActions.loadDatasetReportEditRouteSuccess({
              dataset: {
                token: datasetToken,
                result: {
                  ...dataset.datasetResult,
                  ...(dataset.thoughtspot ? { thoughtspot: dataset.thoughtspot } : {}),
                  ...(dataset.tsPublishMetaData ? { tsPublishMetaData: dataset.tsPublishMetaData } : {}),
                },
                switchView: dataset.switchView,
                details: dataset.details,
              },
            })
          )
        )
      )
    )
  );

  openDeleteDatasetModalFromEditor$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openDeleteDatasetModal),
        withLatestFrom(this.datasetFacade.isDatasetPublished$),
        map(([action, isPublished]) => this.openDialog(true, action.token, isPublished))
      ),
    { dispatch: false }
  );

  openDeleteDatasetModalFromCollection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DatasetActions.openDeleteDatasetModalFromCollectionList,
          DatasetActions.openDeleteDatasetModalFromCollectionTile,
          DatasetActions.openDeleteDatasetModalFromCollectionSidePanel
        ),
        switchMap((action) =>
          this.getDataset(action.token).pipe(
            map((dataset) => ({
              action,
              publishedAt: dataset.details?.thoughtspot?.publishedAt,
            })),
            catchError((error) => {
              console.error('Error fetching dataset during delete:', error);
              return of({
                action,
                publishedAt: false,
              });
            })
          )
        ),
        map(({ action, publishedAt }) => {
          this.openDialog(false, action.token, !!publishedAt);
        })
      ),
    { dispatch: false }
  );

  deleteDatasetFromCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.deleteFromCollection),
      concatMap((action) =>
        this.datasetService.deleteDataset(action.token).pipe(
          map(
            () => DatasetActions.deleteFromCollectionView({ token: action.token }),
            catchError(() => of(DatasetActions.deleteDatasetFail()))
          )
        )
      )
    )
  );

  openPublishModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openPublishModal),
        withLatestFrom(
          this.featureFlagsFacade.asObservable(FeatureFlag.NotebookDatasetPublishUpdates),
          this.dataViewFacade.enhancedDatasetResults$,
          this.reportFacade.isPersonalCollection$
        ),
        tap(([action, publishUpdatesEnabled, datasets, isPersonal]) => {
          const isAnyDatasetPublished = datasets.some((dataset) => dataset?.thoughtspot?.publishedAt);
          // check if updates Enabled and any dataset is published and collection is in personal
          if (publishUpdatesEnabled && !isAnyDatasetPublished && isPersonal) {
            return this.moveSpacePublishService.triggerPublishMoveSpace(action.switchViewToken);
          } else {
            return this.publishDatasetDialogService.open(action.switchViewToken);
          }
        })
      ),
    { dispatch: false }
  );

  publishDatasetAfterMoveSpace$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetAfterMoveSpace),
        tap((action) => this.publishDatasetDialogService.open(action.switchViewToken))
      ),
    { dispatch: false }
  );

  openRepublishModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openRepublishModal),
        tap((action) => this.republishDatasetDialogService.open(action.switchViewToken))
      ),
    { dispatch: false }
  );

  openUnpublishModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.openUnpublishModal),
        tap((action) => this.unpublishDatasetDialogService.open(action.switchViewToken))
      ),
    { dispatch: false }
  );

  publishDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.publishDataset),
      switchMap((action) =>
        this.datasetService
          .publishDataset(action.datasetToken, action.name, action.producerType, action.queryToken)
          .pipe(
            map((datasetPublicationResponse) =>
              DatasetActions.publishDatasetSuccess({
                datasetPublicationResponse,
                name: action.name,
                datasetToken: action.datasetToken,
                producerType: action.producerType,
                queryToken: action.queryToken,
                switchViewToken: action.switchViewToken,
              })
            ),
            catchError((error) => {
              console.error('Error', error);
              return of(DatasetActions.publishDatasetFailure({ error }));
            })
          )
      )
    )
  );

  publishDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetSuccess),
        combineLatestWith(
          this.featureFlagsFacade.featureValueAsObservable<DatasetPublishingFlagStates>(FeatureFlag.DatasetPublishing)
        ),
        tap(([action]) => {
          // Header should update only for resuable dataset during publish
          if (action.producerType !== ProducerTypes.NotebookCell) {
            this.reportFacade.updateReportAfterDatasetPublish(
              action.name,
              action.datasetPublicationResponse.publishedAt
            );
          }
        }),
        tap(([{ datasetPublicationResponse }, publishReusableDatasetFlag]) => {
          if (publishReusableDatasetFlag === DatasetPublishingFlagStates.ENABLED_ALPHA) {
            this.toastService.success('Publication initiated');
          } else {
            const thoughtspotClusterDomain = new URL(datasetPublicationResponse.publishUrl).origin;
            const thoughtspotDataWorkspaceLink = `${thoughtspotClusterDomain}/#/data/tables`;
            this.toastService.success('', {
              disableTimeout: true,
              tapToDismiss: false,
              toastComponent: DatasetPublishSuccessComponent,
              payload: { thoughtspotDataWorkspaceLink },
            });
          }
        }),
        filter(
          ([action]) => action.producerType === ProducerTypes.NotebookCell // Refresh Runs Only for Exported Dataset
        ),
        map(([action]) =>
          CellExportActions.refreshDataViewsAfterExport({
            reportToken: action.datasetToken,
            exportedDatasetToken: action.queryToken ?? '',
          })
        )
      ),
    { dispatch: true }
  );

  publishDatasetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.publishDatasetFailure),
        tap(() => {
          this.toastService.error('Dataset failed to publish. Please try again');
        })
      ),
    { dispatch: false }
  );

  republishDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.republishDataset),
      switchMap((action) =>
        this.datasetService.republishDataset(action.datasetThoughtspotPublicationToken).pipe(
          map(() => DatasetActions.republishDatasetSuccess()),
          catchError((error) => {
            console.error('Error', error);
            return of(DatasetActions.republishDatasetFailure({ error }));
          })
        )
      )
    )
  );

  republishDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.republishDatasetSuccess),
        tap(() => {
          this.toastService.success('Dataset republished');
        })
      ),
    { dispatch: false }
  );

  republishDatasetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.republishDatasetFailure),
        tap(() => {
          this.toastService.error('Dataset failed to republish. Please try again');
        })
      ),
    { dispatch: false }
  );

  unpublishDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.unpublishDataset),
      switchMap(({ datasetToken, switchViewToken, producerType, datasetThoughtspotPublicationToken }) =>
        this.datasetService.unpublishDataset(datasetToken, producerType, datasetThoughtspotPublicationToken).pipe(
          map(() => DatasetActions.unpublishDatasetSuccess({ switchViewToken })),
          catchError((error) => {
            console.error('Error', error);
            return of(DatasetActions.unpublishDatasetFailure({ error }));
          })
        )
      )
    )
  );

  unpublishDatasetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.unpublishDatasetSuccess),
        tap(() => {
          this.toastService.success('Dataset unpublished');
        })
      ),
    { dispatch: false }
  );

  unpublishDatasetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.unpublishDatasetFailure),
        tap(() => {
          this.toastService.error('Dataset failed to unpublish. Please try again');
        })
      ),
    { dispatch: false }
  );

  fetchExportedDatasetSource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.fetchExportedDatasetSource),
      withLatestFrom(this.cellExportFacade.queries$),
      concatMap(([action, queries]) => {
        const matchedDataset = queries?.getRecords()?.find((query: QueryResource) => query.token === action.queryToken);
        if (!matchedDataset) return of(DatasetActions.fetchExportedDatasetSourceFailure({}));
        return this.datasetService.fetchSourceForExportedDataset(matchedDataset).pipe(
          map((response) => DatasetActions.fetchExportedDatasetSourceSuccess({ response })),
          catchError((error) => of(DatasetActions.fetchExportedDatasetSourceFailure({ error })))
        );
      })
    )
  );

  private getDataset(datasetToken: string) {
    return this.switchViewFacade.getDataset(datasetToken);
  }

  private getNotebookCellDataset(datasetToken: string, switchViewToken: string) {
    return this.switchViewFacade.getNotebookCellDataset(datasetToken, switchViewToken);
  }

  public openDialog(isFromEditor: boolean, token: string, isPublished = false) {
    this.confirmDialog.open({
      dialogTitle: `Delete ${isPublished ? 'published' : ''} Dataset?`,
      dialogText: `
          <p>This action is irreversible and will remove the Dataset ${
            isPublished
              ? 'and permanently break dependent content in Analyst Studio and the data workspace.'
              : 'from your Workspace.'
          }</p>
          ${
            isPublished
              ? `
            <p class='bold-text unset-margin'>The following will be deleted:</p>
            <ul>
              <li>any Calculated Fields that were added to the Dataset</li>
              <li>any schedules that were added to the Dataset</li>
            </ul>
            <p class='bold-text unset-margin'>The following will break:</p>
            <ul class='unset-margin'>
              <li>the published table imported in the Data workspace</li>
              <li>any dependent Models or Worksheets</li>
              <li>any dependent Reports or Liveboards</li>
              <li>any dependent Answers</li>
              <li>any dependent calculated fields</li>
            </ul>
            `
              : `
            <p class='bold-text unset-margin'>The following will be deleted:</p>
            <ul>
              <li>any dependent Explorations</li>
              <li>any Calculated Fields that were added to the Dataset</li>
            </ul>
            <p class='bold-text unset-margin'>The following will break:</p>
            <ul class='unset-margin'>
              <li>any dependent Reports</li>
              <li>any dependent Calculated Fields</li>
            </ul>
            `
          }
          `,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      acknowledgeText: isPublished ? `I understand that deleting is permanent and can't be undone` : '',
      confirmAction: () => {
        if (isFromEditor) {
          this.datasetFacade.deleteDataset(token);
        } else {
          this.datasetFacade.deleteFromCollection(token);
        }
      },
      confirmButtonColor: 'alert',
      confirmButtonType: 'primary',
    });
  }

  constructor(
    private actions$: Actions,
    private datasetService: DatasetClientService,
    private switchViewFacade: SwitchViewFacade,
    private toastService: ToastService,
    private datasetFacade: DatasetService,
    private reportFacade: ReportFacade,
    private confirmDialog: ConfirmDialog,
    private publishDatasetDialogService: PublishDialogService,
    private unpublishDatasetDialogService: UnpublishDialogService,
    private republishDatasetDialogService: RepublishDialogService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private cellExportFacade: CellExportFacade,
    private dataViewFacade: DataViewFacade,
    private moveSpacePublishService: MoveSpacePublishService
  ) {}
}
